/**
 * Presentation-only tier copy for the app (taglines, feature bullets, CTAs).
 * Structural plan data (name, price, period, description) comes from the
 * shared source of truth in `shared/plans.json` via `./plans`. Keys must cover
 * every canonical tier id — guarded by `tierCopy.test.ts` so a catalog change
 * can never leave a tier without copy (which would crash the pricing page).
 */

export interface TierPresentation {
  tagline: string
  features: string[]
  cta: string
  ctaVariant: 'primary' | 'secondary'
  highlight: boolean
}

export const TIER_PRESENTATION: Record<string, TierPresentation> = {
  trial: {
    tagline: 'Try the full platform — every feature, for 21 days',
    features: [
      '21 days of Pro access',
      'Up to 500 invoices',
      'Every feature included',
    ],
    cta: 'Start Free Trial',
    ctaVariant: 'secondary',
    highlight: false,
  },
  starter: {
    tagline: 'For independent professionals who value their time',
    features: [
      '50 invoices per month',
      'Intelligent vendor mapping',
      'Portuguese tax support (IVA)',
      'Credit note detection',
      'Email support',
    ],
    cta: 'Choose Light',
    ctaVariant: 'secondary',
    highlight: false,
  },
  growth: {
    tagline: 'For teams who refuse to compromise on precision',
    features: [
      '200 invoices per month',
      'Spend by supplier and cost center',
      'Priority processing',
      'Advanced analytics',
      'Team collaboration',
      'Priority support',
    ],
    cta: 'Choose Full',
    ctaVariant: 'primary',
    highlight: true,
  },
  scale: {
    tagline: 'For high-volume operations that never slow down',
    features: [
      '500 invoices per month',
      'Spend by supplier and cost center',
      'Priority processing',
      'Advanced analytics',
      'Team collaboration',
      'Priority support',
    ],
    cta: 'Choose Pro',
    ctaVariant: 'secondary',
    highlight: false,
  },
  enterprise: {
    tagline: 'Bespoke solutions for organizations at scale',
    features: [
      'For portfolios above 500 invoices/month',
      'Multiple companies, one agreement',
      'Dedicated AI model training',
      'White-glove concierge support',
      'SLA guarantees',
      'Custom integrations',
    ],
    cta: 'Contact Sales',
    ctaVariant: 'secondary',
    highlight: false,
  },
}

